import { AbstractControl, ValidatorFn } from '@angular/forms';

import { TDate } from 'src/app/models/date.model';

export type VehicleWeightRange = { since: number; till: number };

export function validateVehicleWeight(value: unknown, ranges: VehicleWeightRange[]): { message: string } | null {
    if (value === null || value === undefined || value === '') return null;
    if (!ranges?.length) {
        return { message: $localize`Pro tento typ vozidla nelze ověřit povolenou hmotnost.` };
    }
    const weight = Number(value);
    // Codelist 5079 uses inclusive lower and exclusive upper bounds, in whole kg.
    if (Number.isInteger(weight) && ranges.some(range => weight >= range.since && weight < range.till)) {
        return null;
    }
    const allowed = [...ranges].sort((a, b) => a.since - b.since);
    const merged: VehicleWeightRange[] = [];
    allowed.forEach(range => {
        const previous = merged[merged.length - 1];
        if (previous && range.since <= previous.till) previous.till = Math.max(previous.till, range.till);
        else merged.push({ ...range });
    });
    const limits = merged.map(range => `${range.since} - ${range.till - 1}`).join(', ');
    return { message: $localize`Celková hmotnost musí být v povoleném rozsahu ${limits} kg.` };
}


export const licensePlateVal = (control: AbstractControl): { [key: string]: string } | null => {
    if (!control.disabled && control.value) {
        if (
            !control.value.match('.*[0-9].*') ||
            control.value.length < 5 ||
            control.value.length > 8
        ) {
            return { message: $localize`SPZ je ve špatném formátu. Prosíme o kontrolu.` };
        }
    }
    return null;
};

export const engineVolumeVal = (control: AbstractControl): { [key: string]: string } | null => {
    if (!control.disabled && (control.value || control.value === 0)) {
        const min = 0;
        const max = 32000;
        if (control.value < min || control.value > max) {
            return { message: $localize`Objem je mimo rozsah 0 - 32 000 ccm.` };
        }
    }
    return null;
};

export const enginePowerVal = (control: AbstractControl): { [key: string]: string } | null => {
    if (!control.disabled && (control.value || control.value === 0)) {
        const min = 0;
        const max = 10000;
        if (control.value < min || control.value > max) {
            return { message: $localize`Výkon je mimo rozsah 0 - 10 000 kW.` };
        }
    }
    return null;
};

export const numberOfSeatsVal = (control: AbstractControl): { [key: string]: string } | null => {
    if (!control.disabled && (control.value || control.value === 0)) {
        const min = 0;
        const max = 1000;
        if (control.value < min || control.value > max) {
            return { message: $localize`Počet míst k sezení je mimo rozsah 0 - 1 000.` };
        }
    }
    return null;
};

export const weightVal = (vehicleTypeFC: AbstractControl): ValidatorFn => {
    return (control: AbstractControl): { [key: string]: string } | null => {
        if (!control.disabled && (control.value || control.value === 0)) {
            let min = 5;
            let max = 1000000;

            if (vehicleTypeFC.value === 'OA') {
                max = 3500;
            } else if (vehicleTypeFC.value === 'NA') {
                min = 3500;
            }

            if (control.value < min || control.value > max) {
                return { message: $localize`Celková hmotnost je mimo rozsah ${min}:INTERPOLATION: - ${max}:INTERPOLATION: kg.` };
            }
        }

        return null;
    };
};

export const productionYearVal = (max: TDate): ValidatorFn => {
    return (control: AbstractControl): { [key: string]: string } | null => {
        if (!control.disabled && (control.value || control.value === 0) && max) {
            if (control.value <= 1900 || control.value > max.getFullYear()) {
                return { message: $localize`Rok výroby je mimo povolený rozsah.` };
            }
        }
        return null;
    };
};

export const clientsVehiclePriceVal = (control: AbstractControl): { [key: string]: string } | null => {
    if (!control.disabled && (control.value || control.value === 0)) {
        const min = 0;
        const max = 9999999;
        if (control.value < min || control.value > max) {
            return { message: $localize`Cena vozidla je mimo rozsah 0 - 9 999 999 Kč.` };
        }
    }
    return null;
};

export const technicalCertificateNumberVal = (control: AbstractControl): { [key: string]: string } | null => {
    if (!control.value) return null;

    const regExp = new RegExp('^[a-zA-Z]{2}[0-9]{6}$');
    const regExp2 = new RegExp('^[a-zA-Z]{3}[0-9]{6}$');
    if (!regExp.test(control.value) && !regExp2.test(control.value)) {
        return { message: $localize`Číslo technického průkazu je ve špatném formátu. Prosíme o kontrolu.` };
    }
    return null;
};

export const vinVal = (control: AbstractControl): { message: string } | null => {
    if (!control.value)
        return { message: $localize`VIN musí být vyplněn.` };

    if (control.value === '0')
        return { message: $localize`VIN je ve špatném formátu. Prosíme o kontrolu.` };

    if (control.value.length > 17) {
        return { message: $localize`VIN je ve špatném formátu. Prosíme o kontrolu.` };
    }

    return null;
};
